/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import _ from 'underscore' // Npm: underscore library.
import { toast } from 'react-hot-toast' // Npm: React toastify.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import QuickFileUpload from 'components/QuickFileUpload'
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import IpDropDownOptions from 'components/IpDropDownOptions'
import IpUpsert from 'components/IpUpsert'
import IpDelete from 'components/IpDelete'


/*
 * GRAPHS
 */
import IpReadQuery from './__query__/index.ip.read.query'
import IpUploadMutation from './__mutation__/index.ip.upload.mutation'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _skipDifference = 8
  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': _skipDifference,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [ipToSearch, setIpToSearch] = React.useState('')
  const [MutationIpUpload] = useMutation(IpUploadMutation)
  const { 'isOpen': isIpCreateOpen, 'onOpen': onIpCreateOpen, 'onClose': onIpCreateClose } = useDisclosure()
  const { 'isOpen': isIpUpdateOpen, 'onOpen': onIpUpdateOpen, 'onClose': onIpUpdateClose } = useDisclosure()
  const { 'isOpen': isIpDeleteOpen, 'onOpen': onIpDeleteOpen, 'onClose': onIpDeleteClose } = useDisclosure()
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(j => {
      const nextPaginationModel = { ...j, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])
  const _QueryIpReadVariables = React.useMemo(() => ({
    'ipDirectoryId': passOn?.ipDirectoryId,
    ipToSearch,
    'skip': paginationModel?.pageSize * paginationModel?.page,
    'take': paginationModel?.pageSize
  }), [ipToSearch, paginationModel?.page, paginationModel?.pageSize, passOn?.ipDirectoryId])
  const _QueryIpRead = useQuery(IpReadQuery, {
    'variables': _QueryIpReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
  })
  const _onSearchInputChange = React.useRef(_.debounce((value) => {
    _ResetPagination()
    setIpToSearch(value)
  }, 800)).current
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    setFilterModel(__filterModel)
  }, [_ResetPagination])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])
  const _QueryIpReadRowCount = _.pluck(_QueryIpRead?.data?.IpRead, '_totalCount')?.[0]
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryIpReadRowCount && null !== _QueryIpReadRowCount) _rowCountRef.current = _QueryIpReadRowCount

    return _rowCountRef.current
  }, [_QueryIpReadRowCount])
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': false, 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true }) ?? false

  // Extract search from filter model
  React.useEffect(() => {
    const ipFilter = filterModel?.items?.find(item => item.field === 'ip')
    const searchValue = ipFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [_onSearchInputChange, filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await _QueryIpRead.refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [_QueryIpRead.refetch])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 230
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'ip',
      'headerName': 'Ip',
      'description': 'IP address.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 330,
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Created date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 330,
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'width': 330,
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for IP.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 210,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <IpDropDownOptions
        ipId={row?.id}
        ipDirectoryId={passOn?.ipDirectoryId}
        onIpUpdate={onIpUpdateOpen}
        onIpDelete={onIpDeleteOpen}
      />
    }
  ], [onIpUpdateOpen, onIpDeleteOpen, passOn?.ipDirectoryId])

  const rows = React.useMemo(() => _.compact(_QueryIpRead?.data?.IpRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id ?? `ip-directory-ip-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'ip': r?.ip ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD hh:mm:ss A'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD hh:mm:ss A')
  }) : null)), [_QueryIpRead?.data?.IpRead, _successFlags, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className="ip base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          maxH="81vh"
          minH="81vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" gap="12px">
            <Tooltip label="Create New Ip" fontSize="sm">
              <Button
                w="170px"
                leftIcon={<IoMdAdd />}
                onClick={onIpCreateOpen}
                style={buttonStyle}
              >
                Create Ip
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <QuickFileUpload
                onChange={(j) => {
                  // Const assignment.
                  const _ips = Array.from(
                    { length: j?.rows?.length },
                    (__, i) => _.first(j?.rows[i]),
                  );

                  // Execute mutation.
                  return MutationIpUpload({
                    variables: {
                      ipDirectoryId: passOn?.ipDirectoryId,
                      ips: _ips,
                    },
                  }).then((jp) => {
                    // On Successful response from the mutation.
                    if ("UPSERT_SUCCESSFUL" === jp?.data?.IpUpload?.status) {
                      // Toast success.
                      toast(
                        "Successfully uploaded the ips in your ip directory.",
                      );
                    } else {
                      // Toast failure.
                      toast(
                        jp?.data?.IpUpload?.message ??
                          "Failed to upload the ips in your ip directory.",
                      );
                    }
                  });
                }}
                isLoading={false}
              />
              <DownloadToExcel
                cellsData={
                  0 === _QueryIpRead?.data?.IpRead?.length ||
                  !_.every(_.pluck(_QueryIpRead?.data?.IpRead, "status"), (i) =>
                    _successFlags.includes(i),
                  )
                    ? []
                    : _QueryIpRead?.data?.IpRead.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        CreatedAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
                        Ip: item.ip ?? "-",
                      }))
                }
                headersData={["S.No.", "CreatedAt", "UpdatedAt", "Ip"].map(
                  (i) => ({ key: i, label: i }),
                )}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[8, 10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
            loading={_QueryIpRead?.loading}
          />
        </Flex>
        <Modal
          size="md"
          title="Ip Create"
          isOpen={isIpCreateOpen}
          onClose={onIpCreateClose}
        >
          <IpUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size="md"
          title="Ip Update"
          isOpen={isIpUpdateOpen}
          onClose={onIpUpdateClose}
        >
          <IpUpsert />
        </Modal>
        <Modal
          size="md"
          title="Ip Delete"
          isOpen={isIpDeleteOpen}
          onClose={onIpDeleteClose}
        >
          <IpDelete />
        </Modal>
      </Flex>
    </>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
